import {Stack} from '@mui/material'
import React from 'react'

import {RadioButton} from './RadioButton'
import {useRadioButtonGroupStyles} from './RadioButton.styles'
import {RadioButtonGroupProps} from './RadioButton.types'

/**
 * RadioButtonGroup - Container component for managing a group of radio buttons
 *
 * Handles state management and ensures only one option is selected at a time.
 * Automatically renders RadioButton components based on the provided options array.
 *
 * Uses MUI Stack component with standard MUI props for layout.
 *
 * @example
 * // Basic Yes/No confirmation (horizontal, Yes first)
 * <RadioButtonGroup
 *   value={isConfirmed}
 *   onValueChange={setIsConfirmed}
 *   options={[
 *     { label: 'Yes', value: true, variant: 'neutral' },
 *     { label: 'No', value: false, variant: 'warning' }
 *   ]}
 *   direction="row"
 *   testId="order-confirmation"
 * />
 *
 * @example
 * // Plus load question (vertical, No first, both neutral by default)
 * <RadioButtonGroup
 *   value={needsPlusLoad}
 *   onValueChange={setNeedsPlusLoad}
 *   options={[
 *     { label: 'No', value: false },
 *     { label: 'Yes', value: true }
 *   ]}
 *   direction="column"
 *   spacing={1}
 *   testId="plus-load-toggle"
 * />
 *
 * @example
 * // Disabled group
 * <RadioButtonGroup
 *   value={selectedValue}
 *   onValueChange={setSelectedValue}
 *   options={options}
 *   disabled={true}
 *   testId="disabled-group"
 * />
 *
 * @example
 * // Multiple options with individual variants
 * <RadioButtonGroup
 *   value={deliveryMethod}
 *   onValueChange={setDeliveryMethod}
 *   variant="neutral"
 *   options={[
 *     { label: 'Standard delivery', value: 'standard' },
 *     { label: 'Express delivery', value: 'express' },
 *     { label: 'Cancel delivery', value: 'cancel', variant: 'warning' }
 *   ]}
 *   direction="column"
 *   testId="delivery-method"
 * />
 */
export const RadioButtonGroup = <T,>({
  value,
  onValueChange,
  options,
  disabled = false,
  variant = 'neutral',
  direction = 'row',
  spacing = 2,
  testId,
  className
}: RadioButtonGroupProps<T>) => {
  const {classes} = useRadioButtonGroupStyles({disabled})

  return (
    <Stack
      direction={direction}
      spacing={spacing}
      className={`${classes.container} ${className || ''}`}
      data-test-id={testId}
      role="radiogroup"
    >
      {options.map((option, index) => {
        const isSelected = value === option.value
        const isDisabled = disabled || option.disabled
        const optionVariant = option.variant || variant

        return (
          <RadioButton
            key={String(option.value)}
            selected={isSelected}
            onPress={() => !isDisabled && onValueChange(option.value)}
            label={option.label}
            disabled={isDisabled}
            variant={optionVariant}
            testId={option.testId ?? (testId ? `${testId}-option-${index}` : undefined)}
          />
        )
      })}
    </Stack>
  )
}
